import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { cpfCnpjValidatorDirective } from 'app/core/helpers/cpfCnpjValidatorDirective';
import { DateValidatorDirective } from 'app/core/helpers/dateValidatorDirective';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { ErrorHandlerService } from 'app/core/http/services/error-handler.service';
import { PractitionerService } from 'app/core/http/services/practitioner.service';
import { genderTranslated } from 'app/core/interfaces/currentUser';
import { Practitioner } from 'app/core/interfaces/practitioner';
import { ageValidator } from 'app/core/validators/AgeValidator';
import { validateImageFile } from 'app/core/validators/validateImageFile';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { switchMap } from 'rxjs/operators';



@Component({
  selector: 'app-practitioner-registration',
  templateUrl: './practitioner-registration.component.html',
  styleUrls: ['./practitioner-registration.component.css'],
})
export class PractitionerRegistrationComponent extends BaseComponent implements OnInit {
  @Input() practitioner: Practitioner = <Practitioner>{};
  @Input() mode = 'UPDATE';
  @Output() onUpdatePractitioner = new EventEmitter<any>();

  public form: FormGroup;
  public userEmail: string = '';
  public invalidForm = false;
  public titles = ['Dados Cadastrais', 'Dados Profissionais', 'Endereço'];
  public genders: genderTranslated[] = [];
  public isSaving = false;

  profilePictureUrl: string | ArrayBuffer | null = null;
  selectedFileName: string | null = null;

  constructor(
    private fb: FormBuilder,
    private injector: Injector,
    private practitionerService: PractitionerService,
    private cognitoService: CognitoService,
    private cdr: ChangeDetectorRef,
    private errorHandler: ErrorHandlerService
  ) {
    super(injector);

    this.form = this.fb.group({
      practitioner_id: new FormControl(null, [Validators.nullValidator]),
      name: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-zÀ-ÿ\s]+$/)]),
      birthDate: new FormControl('', [Validators.required, DateValidatorDirective.validate, Validators.pattern(/^[0-9]{8}$/), ageValidator(18, 120)]),
      cpf: new FormControl('', [Validators.required, cpfCnpjValidatorDirective.validate, Validators.pattern(/^[0-9]{11}$/)]),
      gender: new FormControl('', [Validators.required]),
      mobile: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{11}$/)]),
      email: new FormControl(this.userEmail, [Validators.nullValidator]),
      phone: new FormControl(''),
      active: new FormControl(true),
      photo: new FormControl(''),
    });
  }

  async ngOnInit() {
    this.showSuccessMessage = false;
    this.showErrorMessage = false;
    this.genders = this.layout.initTranslatedgender();
    await this.getUserEmail();
    await this.createOrUpdate();

    if (this.mode === 'UPDATE' && localStorage.getItem('isFirstAccess') === 'false') {
      this.getPractitioner();
    }

    if (this.mode === 'CREATE') {
      this.form.patchValue({
        practitioner_id: this.practitioner?.practitioner_id,
        name: this.practitioner?.name,
        birthDate: this.practitioner.birthDate,
        cpf: this.practitioner.cpf ?? '',
        gender: this.practitioner.gender ?? '',
        mobile: this.practitioner.mobile ?? '',
        email: this.userEmail,
        phone: this.practitioner.phone ?? '',
        active: true,
        photo: this.practitioner.photo ?? '',
      });
    }

  }

  allowOnlyLetters(event: KeyboardEvent): void {
    const inputChar = event.key;
    if (!/^[A-Za-zÀ-ÿ\s]$/.test(inputChar)) {
      event.preventDefault();
    }
  }

  onPasteName(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData?.getData('text') ?? '';
    const validNameRegex = /^[A-Za-zÀ-Üà-ü\s]+$/;
    if (!validNameRegex.test(clipboardData)) {
      event.preventDefault();
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    const photoControl = this.form.controls['photo'];

    if (!file) {
      return;
    }

    const validationResult = validateImageFile(file, 4);

    if (!validationResult.isValid) {
      photoControl.setErrors(validationResult.errors);
      this.profilePictureUrl = null;
      this.selectedFileName = null;

      return;
    }

    photoControl.setErrors(null);
    this.selectedFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const base64 = reader.result as string;
        this.profilePictureUrl = base64;
        this.form.controls['photo'].setValue(base64);
      }
    };
    reader.readAsDataURL(file);
  }

  private async getUserEmail() {
    const { standardAttributes } = await this.cognitoService.getUserAttributes();
    this.userEmail = standardAttributes['email'];
    this.form.patchValue({ email: this.userEmail });
  }

  async createOrUpdate() {
    const userAttributes = await this.cognitoService.getUserAttributes();
    const { customAttributes } = userAttributes;
    const firstAccess = customAttributes['firstAccess'];
    localStorage.setItem('isFirstAccess', firstAccess);
    this.mode = localStorage.getItem('isFirstAccess') === 'true' ? 'CREATE' : 'UPDATE';
  }

  validateForm() {
    return !this.form.valid;
  }

  private getPractitioner() {
    const practitionerId = this.getUserLogged().id;
    this.practitionerService.showNew(practitionerId).subscribe({
      next: (practitioner: Practitioner) => {
        this.practitioner = practitioner;

        let formattedMobile = this.practitioner.mobile || '';
        if (formattedMobile.startsWith('55')) {
          formattedMobile = formattedMobile.replace('55', '');
        }

        this.form.patchValue({
          practitioner_id: this.practitioner.practitioner_id,
          name: this.practitioner?.name,
          birthDate: this.practitioner?.birthDate,
          cpf: this.practitioner.cpf,
          gender: this.practitioner.gender,
          mobile: formattedMobile,
          email: this.userEmail,
          photo: this.practitioner.photo,
        });
        this.profilePictureUrl = this.practitioner.photo || this.defaultPractitionerPhoto;

        this.onUpdatePractitioner.emit(practitioner)
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  save() {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.form.markAllAsTouched();

    const errors = this.collectErrors();
    if (errors.length) {
      this.messageError = errors.join('\n');
      this.showErrorMessage = true;
      return;
    }

    this.isSaving = true;
    const practitionerId = this.getUserLogged().id;
    const practitionerFormValues = { ...this.practitioner, ...this.form.value };
    practitionerFormValues.mobile = `55${practitionerFormValues.mobile}`;


    this.practitionerService.updateNew(practitionerId, practitionerFormValues).subscribe({
      next: (practitioner: Practitioner) => {
        this.isSaving = false;
        this.showErrorMessage = false;
        this.showSuccessMessage = true;

       this.onUpdatePractitioner.emit(practitioner)
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving = false;
        this.showErrorMessage = true;

        this.messageError = error.error?.message || this.errorHandler.getErrorMessage(error);
      },
    });
  }

  create() {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.form.markAllAsTouched();

    const errors = this.collectErrors();
    if (errors.length) {
      this.messageError = errors.join('\n');
      this.showErrorMessage = true;
      return;
    }

    this.isSaving = true;
    const practitionerFormValues = this.form.value;
    practitionerFormValues.mobile = `55${practitionerFormValues.mobile}`;

    this.practitionerService
      .createNew(practitionerFormValues)
      .pipe(
        switchMap((practitioner: Practitioner) => {
          this.auth.setUser(practitioner, localStorage.getItem('userType'), localStorage.getItem('token'), localStorage.getItem('token_id'));
          this.onUpdatePractitioner.emit(practitioner)
          
          setTimeout(() => {
            this.router.navigate(
              ['medico/meus-dados'],
              { queryParams: { tab: 'especialidade' } }
            );
            this.loading = false;
          }, 500);

          return this.cognitoService.updateFirstAccess();
        })
      )
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.showErrorMessage = false;
          this.showSuccessMessage = true;
          this.getPractitioner();
        },
        error: (error: HttpErrorResponse) => {

          this.isSaving = false;

          this.showErrorMessage = true;

          if (error.status == 413) {
            this.messageError = 'Imagem enviada ultrapassa o tamanho máximo permitido de 4MB'
            return
          }

  
          this.messageError = error.error?.message || this.errorHandler.getErrorMessage(error);
        },
      });
  }

  private collectErrors(): string[] {
    const ctrls: any = this.form.controls;
    const errors: string[] = [];

    const requiredFields: string[] = [];
    if (ctrls.name.hasError('required')) {
      requiredFields.push('Nome');
    }
    if (ctrls.birthDate.hasError('required')) {
      requiredFields.push('Data de nascimento');
    }
    if (ctrls.cpf.hasError('required')) {
      requiredFields.push('CPF');
    }
    if (ctrls.gender.hasError('required')) {
      requiredFields.push('Gênero');
    }
    if (ctrls.mobile.hasError('required')) {
      requiredFields.push('Celular');
    }
    if (requiredFields.length) {
      const lista = requiredFields.map((f) => `'${f}'`).join(', ');
      errors.push(`O campo: ${lista} é obrigatório`);
    }

    if (ctrls.name.hasError('pattern')) {
      errors.push('Nome deve conter apenas letras');
    }
    if (ctrls.birthDate.hasError('pattern') || ctrls.birthDate.hasError('dateInvalid')) {
      errors.push('Data de nascimento deve estar no formato DD/MM/AAAA');
    }

    if (ctrls.birthDate.hasError('underAge')) {
      errors.push('É preciso ser maior de idade');
    }

    if (ctrls.birthDate.hasError('overAge')) {
      errors.push('Entre com uma idade realista (Idade não pode exceder 120 anos).');
    }

    if (ctrls.cpf.hasError('pattern')) {
      errors.push('CPF inválido');
    }
    if (ctrls.mobile.hasError('pattern')) {
      errors.push('Celular deve conter 11 dígitos numéricos');
    }

    return errors;
  }
}
